/**
 * 트렌드 리포트 그래프의 차트 데이터 — 기간 안의 액티비티별(Chart by Activity) / 선수별(Chart by Player) 한 줄씩.
 * 트렌드 화면(trend/page.tsx)이 만들던 것과 같은 규칙을 PDF 빌더도 쓰려고 공용으로 뺐다 (2026-10-02 요청).
 *
 * 일일 리포트의 buildChartData(common/sections/graph.ts)와 역할은 같고, 값의 출처만 다르다 —
 * 트렌드는 선수·액티비티마다 기간 집계(average/total)를 들고 있다.
 */
import { isNil } from "lodash"

import { AxisY, ChartData, ChartRow, Sort } from "@/app/report/common/constructor"
import { SORT_ORDER } from "@/constants/setting"
import { DIVE_METRICS, SECTION_TYPE } from "@/app/report/common/constants"
import {
  getDecimalPoint,
  getSummaryImplValue,
  parseSummary,
  parseUnit,
} from "@/app/report/common/tools"
import type { GraphSettings, MetricMapType } from "@/app/report/common/type"
import type { Unit } from "@/types/setting"
import type { ReportTemplate } from "@/types/report"
import { globalDate } from "@/utils/format"

/** 트렌드 템플릿 안의 그래프 차트 하나 — 어느 섹션의 몇 번째 차트인지로 가리킨다 */
export interface TemplateTrendChart {
  sectionIndex: number
  chartIndex: number
  settings: GraphSettings
}

/** 트렌드 템플릿에서 선수 그래프(playerGraph) 또는 액티비티 그래프(activityGraph) 차트를 모은다 */
export const getTemplateTrendCharts = (
  template: ReportTemplate | null | undefined,
  kind: "player" | "activity",
): TemplateTrendChart[] => {
  const sectionType = kind === "player" ? SECTION_TYPE.PLAYER_GRAPH : SECTION_TYPE.ACTIVITY_GRAPH

  return (template?.content ?? []).flatMap((section, sectionIndex) =>
    section?.section === sectionType && Array.isArray(section.settings)
      ? (section.settings as GraphSettings[]).map((settings, chartIndex) => ({
          sectionIndex,
          chartIndex,
          settings,
        }))
      : [],
  )
}

/** 아이템이 가리키는 차트. 없거나 템플릿에서 사라졌으면 첫 차트 */
export const resolveTemplateTrendChart = (
  charts: TemplateTrendChart[],
  sectionIndex?: number,
  chartIndex?: number,
): TemplateTrendChart | null =>
  charts.find(
    (chart) => chart.sectionIndex === sectionIndex && chart.chartIndex === (chartIndex ?? 0),
  ) ??
  charts[0] ??
  null

/** 기간 집계를 들고 있는 선수 한 명 (ReadTrendPlayerSummaryResponse 의 한 줄) */
export interface TrendPlayerRow {
  idfPlayer: number
  playerName: string
  position: string
  uniformNumber: number
  numOfActivity: number
  average: unknown
  total: unknown
}

/** 기간 집계를 들고 있는 액티비티 하나 (ReadTrendPlayerActivitySummaryResponse 의 한 줄) */
export interface TrendActivityRow {
  idfActivity: number
  activityName: string
  date: string | number
  numOfPlayers: number
  average: unknown
  total: unknown
}

interface CommonArgs {
  settings: GraphSettings
  metricMap: MetricMapType
  selectedUnitList: Unit[]
  t: (key: string) => string
  translateKey: (key: string) => string | undefined
}

/** 축 설정 — 트렌드 화면과 같은 규칙(평균 + times 단위는 소수 1자리) */
function buildAxisY({
  settings,
  metricMap,
  selectedUnitList,
  t,
  translateKey,
}: CommonArgs): AxisY[] {
  return settings.yAxis.map((y) => {
    const unit = parseUnit(y.metric, selectedUnitList, metricMap) ?? null
    let decimalPoint = getDecimalPoint(y.metric, unit)

    if (settings.calcType === "average" && unit === "times") {
      decimalPoint = 1
    }

    return new AxisY({
      name: translateKey(y.metric) || String(t("metric." + y.metric)),
      key: y.metric,
      unit,
      decimalPoint,
      color: y.color,
      chartType: y.chartType,
      flagShow: true,
      manualScaleActive: y.manualScaleActive,
      manualScaleValue: y.manualScaleValue,
    })
  })
}

const resolveSort = (settings: GraphSettings, defaultKey: string) =>
  settings.sortSetting
    ? new Sort(settings.sortSetting.key, settings.sortSetting.order.toUpperCase() as "ASC" | "DESC")
    : new Sort(defaultKey, SORT_ORDER.ASC)

/** Chart by Player (트렌드) — 선수마다 한 줄, 값은 기간 평균 또는 합계 */
export function buildTrendPlayerChartData(
  args: CommonArgs & { players: TrendPlayerRow[] },
): ChartData {
  const { settings, metricMap, players } = args
  const axisY = buildAxisY(args)
  const sort = resolveSort(settings, "uniformNumber")

  const rows = players.map((player) => {
    const row = new ChartRow()
    row.name = `${player.uniformNumber} ${player.playerName}`
    row.header.set("uniformNumber", player.uniformNumber ?? null)
    row.header.set("playerName", player.playerName ?? null)
    row.header.set("position", player.position ?? null)
    row.header.set("activity", player.numOfActivity ?? null)

    const source = parseSummary(
      (settings.calcType === "total" ? player.total : player.average) as never,
    )
    const isGK = player.position === "GK"
    settings.yAxis.forEach((y) => {
      if (!isGK && DIVE_METRICS.includes(y.metric)) {
        row.data.push(null)

        return
      }
      const { value } = getSummaryImplValue(y.metric, source, metricMap)
      row.data.push(isNil(value) ? null : Number(value))
    })

    return row
  })

  return new ChartData(settings, sort, axisY, rows)
}

/** Chart by Activity (트렌드) — 액티비티마다 한 줄, 값은 그 액티비티의 평균 또는 합계 */
export function buildTrendActivityChartData(
  args: CommonArgs & { activities: TrendActivityRow[] },
): ChartData {
  const { settings, metricMap, activities } = args
  const axisY = buildAxisY(args)
  const sort = resolveSort(settings, "date")

  const rows = activities.map((activity) => {
    const row = new ChartRow()
    const date = globalDate(activity.date as never, true)
    row.name = date
    row.header.set("activityName", activity.activityName)
    row.header.set("date", date)
    row.header.set("playerCount", activity.numOfPlayers)

    const source = parseSummary(
      (settings.calcType === "total" ? activity.total : activity.average) as never,
    )
    settings.yAxis.forEach((y) => {
      const { value } = getSummaryImplValue(y.metric, source, metricMap)
      row.data.push(isNil(value) ? null : Number(value))
    })

    return row
  })

  return new ChartData(settings, sort, axisY, rows)
}
